// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, fmt } from './shared';

const calcRata = (importo, tanPerc, durMesi) => {
  const r = tanPerc / 100 / 12;
  if (r === 0 || importo <= 0) return importo / Math.max(durMesi, 1);
  return importo * (r * Math.pow(1 + r, durMesi)) / (Math.pow(1 + r, durMesi) - 1);
};

const DEDUC_PIVA = {
  privato: { iva: 0, costo: 0, label: 'Nessuna deduzione' },
  piva_forfettario: { iva: 0, costo: 20, label: 'P.IVA forfettario: 20% costo deducibile' },
  piva_ordinario: { iva: 40, costo: 20, label: 'P.IVA ordinario: 40% IVA + 20% costo (uso misto)' },
  azienda: { iva: 40, costo: 80, label: 'Azienda (uso strumentale): 40% IVA + 80% costo' },
};

const LeasingAuto = () => {
  const [prezzoAuto, setPrezzoAuto] = useState('25000');
  const [anticipo, setAnticipo] = useState('3000');
  const [durataLeas, setDurataLeas] = useState('48');
  const [canone, setCanone] = useState('350');
  const [riscatto, setRiscatto] = useState('5000');
  const [tanFin, setTanFin] = useState('6.5');
  const [tipoUtente, setTipoUtente] = useState('privato');

  const P = parseFloat(prezzoAuto) || 0;
  const A = parseFloat(anticipo) || 0;
  const D = parseInt(durataLeas) || 48;
  const K = parseFloat(canone) || 0;
  const R = parseFloat(riscatto) || 0;
  const T = parseFloat(tanFin) || 0;
  const ded = DEDUC_PIVA[tipoUtente];

  // Scenario 1: Leasing + riscatto
  const totLeasing = A + K * D + R;
  const iva = P * 0.22;
  const risparmioIva = iva * (ded.iva / 100);
  const risparmioCosto = totLeasing * (ded.costo / 100);
  const nettLeasing = totLeasing - risparmioIva - risparmioCosto;

  // Scenario 2: Finanziamento
  const rata = calcRata(P - A, T, D);
  const totFin = A + rata * D;
  const risparmioFinCosto = totFin * (ded.costo / 100);
  const nettFin = totFin - risparmioFinCosto;

  // Scenario 3: Cash
  const nettCash = P - P * (ded.costo / 100);

  const rows = [
    { label: 'Anticipo', leas: A, fin: A, cash: P },
    { label: 'Rate mensili', leas: K * D, fin: rata * D, cash: 0 },
    { label: 'Riscatto finale', leas: R, fin: 0, cash: 0 },
    { label: 'Totale lordo', leas: totLeasing, fin: totFin, cash: P, bold: true },
    ...(tipoUtente !== 'privato' ? [{ label: 'Risparmio fiscale', leas: -(risparmioIva + risparmioCosto), fin: -risparmioFinCosto, cash: -P * (ded.costo / 100), green: true }] : []),
    { label: 'Totale netto', leas: nettLeasing, fin: nettFin, cash: nettCash, bold: true, highlight: true },
  ];

  const best = [nettLeasing, nettFin, nettCash].indexOf(Math.min(nettLeasing, nettFin, nettCash));
  const colColors = ['#C9A84C', '#5B8DB8', '#3D7A52'];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="leasing-auto-calc">
      <EducBox text="Il leasing sembra conveniente per la rata bassa, ma il costo totale su 4-5 anni può superare un finanziamento o persino l'acquisto cash. Per P.IVA e aziende cambiano le deducibilità fiscali. Questo calcolatore confronta i tre scenari sul costo netto reale." />

      {/* Toggle tipo utente */}
      <div className="mb-6">
        <label className="text-[#888780] text-sm block mb-2">Tipo di utente</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(DEDUC_PIVA).map(([k, v]) => (
            <button key={k} onClick={() => setTipoUtente(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${tipoUtente === k ? 'bg-[#C9A84C] text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}
              data-testid={`tipo-${k}`}>
              {k === 'privato' ? 'Privato' : k === 'piva_forfettario' ? 'P.IVA Forfettario' : k === 'piva_ordinario' ? 'P.IVA Ordinario' : 'Azienda'}
            </button>
          ))}
        </div>
        {tipoUtente !== 'privato' && (
          <p className="text-xs text-[#888780] mt-2">Regime fiscale: {ded.label}</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[#888780] text-sm block mb-1">Prezzo auto (€)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
            <input type="number" value={prezzoAuto} onChange={e => setPrezzoAuto(e.target.value)}
              className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Anticipo (€)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
            <input type="number" value={anticipo} onChange={e => setAnticipo(e.target.value)}
              className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Durata: {D} mesi</label>
          <input type="range" min={12} max={72} step={12} value={D} onChange={e => setDurataLeas(e.target.value)}
            className="w-full accent-[#C9A84C]" />
          <div className="flex justify-between text-xs text-[#888780] mt-0.5"><span>12</span><span>72 mesi</span></div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Canone mensile leasing (€)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
            <input type="number" value={canone} onChange={e => setCanone(e.target.value)}
              className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Valore riscatto finale (€)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
            <input type="number" value={riscatto} onChange={e => setRiscatto(e.target.value)}
              className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">TAN finanziamento % (scenario 2)</label>
          <input type="number" step="0.1" value={tanFin} onChange={e => setTanFin(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Tabella 3-colonne */}
      <div className="overflow-x-auto rounded-xl mb-6" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <th className="py-3 px-4 text-left text-[#888780] font-medium">Voce</th>
              {[
                { label: 'Leasing + riscatto', idx: 0 },
                { label: 'Finanziamento', idx: 1 },
                { label: 'Acquisto cash', idx: 2 },
              ].map(({ label, idx }) => (
                <th key={idx} className="py-3 px-4 text-right font-semibold" style={{ color: best === idx ? colColors[idx] : '#888780' }}>
                  {label} {best === idx && '★'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={`border-b border-white/[0.04] ${row.highlight ? 'font-bold' : ''}`}
                style={{ background: row.highlight ? 'rgba(255,255,255,0.03)' : i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                <td className="py-2.5 px-4 text-[#888780]">{row.label}</td>
                {[row.leas, row.fin, row.cash].map((v, ci) => (
                  <td key={ci} className="py-2.5 px-4 text-right"
                    style={{ color: row.green ? '#3D7A52' : row.highlight && best === ci ? colColors[ci] : '#E8E8E4' }}>
                    {row.green && v < 0 ? `−€${fmt(-v)}` : `€${fmt(v)}`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* P.IVA deductibility box */}
      {tipoUtente !== 'privato' && (
        <div className="mb-4 p-4 rounded-xl text-sm" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
          <p className="font-medium text-[#C9A84C] mb-2">Deducibilità fiscale auto — {ded.label}</p>
          <div className="grid sm:grid-cols-3 gap-3 text-[#888780]">
            <div><p className="text-xs">Deducibilità IVA</p><p className="text-lg font-bold text-[#E8E8E4]">{ded.iva}%</p></div>
            <div><p className="text-xs">Deducibilità costo</p><p className="text-lg font-bold text-[#E8E8E4]">{ded.costo}%</p></div>
            <div><p className="text-xs">Risparmio totale stimato</p><p className="text-lg font-bold text-[#3D7A52]">€{fmt(risparmioIva + risparmioCosto)}</p></div>
          </div>
          {tipoUtente === 'piva_forfettario' && (
            <p className="text-xs mt-2 text-[#888780]">Il forfettario non ha IVA da recuperare. Limite costo: €18.076/anno per uso promiscuo.</p>
          )}
        </div>
      )}

      <p className="text-xs text-[#888780] italic">Calcolo orientativo. I valori reali dipendono dalle condizioni contrattuali e dal regime fiscale applicato. Consulta un commercialista per ottimizzare la deducibilità.</p>
</motion.div>
  );
};

export default LeasingAuto;
